import React from 'react';
import { Typography } from '@zydalabs/zac-react';
import useTranslation from 'next-translate/useTranslation';
import { StyledPageWrapper } from 'common/styles';
import StoreFrontHeader from 'common/components/StoreFrontHeader';
import { useLayout } from 'service/hooks';
import { getFullStoreURL } from 'common/utils';
import ServiceConfigs from 'service/config';
import router from 'next/router';
import styled from 'styled-components';

// Styled components for the bullet list
const BulletItem = styled.div`
  margin-left: 24px;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
`;

const BulletPoint = styled.span`
  margin-right: 8px;
`;

const PrivacyPolicyPage = () => {
  const { t } = useTranslation('policy');
  const { data: storeData } = useLayout();
  const storeBaseUrl = getFullStoreURL();
  const lang = router.locale;
  const storeName = lang === 'en' ? storeData?.titleEn : storeData?.titleAr || '';
  const domainOrUrl = storeBaseUrl || ServiceConfigs.getOrderFastStore() || window.location.host;

  // Replace placeholders in the content
  const getFormattedContent = () => {
    let content = t('privacyPolicy.content');
    content = content.replace(/\[store title english\]/g, storeName);
    content = content.replace(/\[domain name \/ orderfast url if no custom domain\]/g, domainOrUrl);
    return content;
  };

  // Replace placeholder in title
  const getFormattedTitle = () => {
    let title = t('privacyPolicy.title');
    title = title.replace(/\[domain name \/ orderfast url if no custom domain\]/g, domainOrUrl);
    return title;
  };

  // Split content by line breaks for proper display
  const contentLines = getFormattedContent().split('\n');

  // Items to be displayed as bullet points
  const bulletItems = ['Facebook', 'Google', 'Tiktok', 'Snapchat', 'Twitter', 'And others'];

  // Track if we're currently in a bullet list section
  let inBulletSection = false;
  const bulletItemsSet = new Set(bulletItems);

  return (
    <StyledPageWrapper>
      <StoreFrontHeader />
      <div style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
        <Typography variant="heading24" mb="24px">
          {getFormattedTitle()}
        </Typography>

        <div>
          {contentLines.map((line, index) => {
            // Create a unique key based on content and position
            const uniqueKey = `line-${index}-${line.length > 10 ? line.substring(0, 10) : line}`;

            // Check if the current line is one of our bullet items
            if (bulletItemsSet.has(line.trim())) {
              inBulletSection = true;
              return (
                <BulletItem key={uniqueKey}>
                  <BulletPoint>•</BulletPoint>
                  <Typography variant="body16">{line.trim()}</Typography>
                </BulletItem>
              );
            }

            // Reset flag when we exit the bullet list section
            if (inBulletSection && line.trim() !== '' && !bulletItemsSet.has(line.trim())) {
              inBulletSection = false;
            }

            // Check if line looks like a heading (all caps or ends with colon)
            if (line.toUpperCase() === line && line.length > 5) {
              return (
                <Typography key={uniqueKey} variant="heading18" mt="24px" mb="16px">
                  {line}
                </Typography>
              );
            }

            // Check if line is empty (for spacing)
            if (line.trim() === '') {
              return <div key={uniqueKey} style={{ height: '16px' }} />;
            }

            // Regular paragraph text
            return (
              <Typography key={uniqueKey} variant="body16" mb="16px">
                {line}
              </Typography>
            );
          })}
        </div>
      </div>
    </StyledPageWrapper>
  );
};

export default PrivacyPolicyPage;
